import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "customer-forecast-timeline__table-container content-container" }
const _hoisted_2 = { class: "content-container__timeline-table timeline-table" }
const _hoisted_3 = { class: "timeline-table__header" }
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = {
  class: "loading-info",
  colspan: "1000"
}
const _hoisted_7 = { class: "loading-info__content" }
const _hoisted_8 = { class: "loading-info__title" }
const _hoisted_9 = { key: 1 }
const _hoisted_10 = {
  class: "no-data",
  colspan: "1000"
}
const _hoisted_11 = { key: 2 }
const _hoisted_12 = {
  class: "error",
  colspan: "1000"
}
const _hoisted_13 = ["rowspan"]
const _hoisted_14 = { class: "timeline-table__row-cell timeline-table__row-cell--month timeline-table__row-cell--sticky" }

import { i18n } from '@/i18n';
import { IYearMonth } from '@/coreDomainsModule/date';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { IForecastTimelineTableItem } from '@/coreDomainsModule/entities/forecastTimeline';
import { computed, onMounted, ref, watch } from 'vue';
import { isError, isLoading, isNone, isSuccess, isZeroMatch, LoadingState } from '@/coreDomainsModule/uiLoadingState';
import loadingAnimation from '@/assets/loadingAnimation.json';
import { LottieAnimation } from 'lottie-web-vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { ForecastTimelineService } from '@/coreDomainsModule/services/forecastTimelineService';

/**
 * 内示遍歴テーブルのデータ用の型
 * [縦軸年][縦軸月][横軸年][横軸月] => 内示 | 販売実績
 */
type ForecastTimelineTableItemData = Record<number, Record<number, Record<number, Record<number, number | undefined>>>>;


export default /*@__PURE__*/_defineComponent({
  __name: 'ForecastTimelineTable',
  props: {
    headerMonthList: {},
    rowMonthList: {},
    tableItemData: {},
    propLoadingState: {}
  },
  emits: ["reload"],
  setup(__props: any, { emit: __emit }) {

const t = i18n.t;

const loadingState = ref<LoadingState>({ status: { type: 'loading' } });
const useTableItemData = ref<ForecastTimelineTableItemData>({});

// props
const props = __props;

const emits = __emit;

watch(
  () => props.propLoadingState,
  (newValue) => {
    loadingState.value = newValue;
  }
);

watch(
  () => props.tableItemData,
  (newValue) => {
    if (newValue) setTableItemData(newValue);
  }
);

onMounted(() => {
  loadingState.value = props.propLoadingState;
});

/**
 * テーブルのデータを整形する
 * @param data - テーブルのデータ
 */
const setTableItemData = (data: Set<IForecastTimelineTableItem>) => {
  const tableItemData = data;
  const tableItemDataMap: ForecastTimelineTableItemData = {};

  /**
   * Recordオブジェクトを初期化する
   * @param year - 縦軸年
   * @param month - 縦軸月
   * @param horizontalYear - 横軸年
   * @param horizontalMonth - 横軸月
   */
  const _initRecords = (year: number, month: number, horizontalYear: number, horizontalMonth: number) => {
    if (!tableItemDataMap[year]) {
      tableItemDataMap[year] = {};
    }
    if (!tableItemDataMap[year][month]) {
      tableItemDataMap[year][month] = {};
    }
    if (!tableItemDataMap[year][month][horizontalYear]) {
      tableItemDataMap[year][month][horizontalYear] = {};
    }
    if (!tableItemDataMap[year][month][horizontalYear][horizontalMonth]) {
      tableItemDataMap[year][month][horizontalYear][horizontalMonth] = undefined;
    }
  };

  tableItemData.forEach((item) => {
    // 変数抽出
    const { verticalYearMonth, horizontalYearMonth, amount } = item;
    // レコードを初期化
    _initRecords(verticalYearMonth.year, verticalYearMonth.month, horizontalYearMonth.year, horizontalYearMonth.month);
    // データを格納
    tableItemDataMap[verticalYearMonth.year][verticalYearMonth.month][horizontalYearMonth.year][horizontalYearMonth.month] = amount;
  });

  useTableItemData.value = tableItemDataMap;
};

/**
 * テーブルの項目の種類を取得する
 * @param receivedYearMonth - 受け取った年月
 * @param targetYearMonth - 表示する年月
 */
const getTableCellItem = (receivedYearMonth: IYearMonth, targetYearMonth: IYearMonth): number | undefined => {
  const tableItemData = useTableItemData.value;

  if (
    tableItemData[receivedYearMonth.year] &&
    tableItemData[receivedYearMonth.year][receivedYearMonth.month] &&
    tableItemData[receivedYearMonth.year][receivedYearMonth.month][targetYearMonth.year] &&
    tableItemData[receivedYearMonth.year][receivedYearMonth.month][targetYearMonth.year][targetYearMonth.month]
  ) {
    return tableItemData[receivedYearMonth.year][receivedYearMonth.month][targetYearMonth.year][targetYearMonth.month];
  }

  return undefined;
};

/**
 * テーブルに表示するセルの内容を取得する
 * @param receivedYearMonth - 受け取った年月
 * @param targetYearMonth - 表示する年月
 */
const displayTableCell = (receivedYearMonth: IYearMonth, targetYearMonth: IYearMonth) => {
  const tableItem = getTableCellItem(receivedYearMonth, targetYearMonth);
  return tableItem ?? '';
};

/**
 * テーブルのセルのクラスを取得する
 * @param receivedYearMonth - 受け取った年月
 * @param targetYearMonth - 表示する年月
 * @param headerIndex - ヘッダーのインデックス
 */
const getTableCellClass = (receivedYearMonth: IYearMonth, targetYearMonth: IYearMonth, headerIndex: number) => {
  const tableItemType = ForecastTimelineService.getTableCellType(receivedYearMonth, targetYearMonth);

  return {
    'timeline-table__row-cell--forecast': tableItemType === 'forecast',
    'timeline-table__row-cell--sales-result': tableItemType === 'salesResult',
    'timeline-table__row-cell--end-column': headerIndex === props.headerMonthList.length - 1,
  };
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("table", _hoisted_2, [
      _createElementVNode("thead", null, [
        _createElementVNode("tr", _hoisted_3, [
          (_unref(isSuccess)(loadingState.value.status))
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(2, (i) => {
                  return _createElementVNode("th", {
                    key: i,
                    class: "timeline-table__header-cell timeline-table__header-cell--accessory timeline-table__header-cell--corner-sticky"
                  }, _toDisplayString(''))
                }), 64)),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.headerMonthList, (month, index) => {
                  return (_openBlock(), _createElementBlock("th", {
                    key: index,
                    class: _normalizeClass(["timeline-table__header-cell timeline-table__header-cell--month timeline-table__header-cell--sticky", { 'timeline-table__header-cell--end-column': index === _ctx.headerMonthList.length - 1 }])
                  }, _toDisplayString(_unref(DateService).convertIYearMonthToYYYYMMDivideSlash(month)), 3))
                }), 128))
              ], 64))
            : (_openBlock(), _createElementBlock("th", _hoisted_4, _toDisplayString(), 1))
        ])
      ]),
      _createElementVNode("tbody", null, [
        (_unref(isLoading)(loadingState.value.status))
          ? (_openBlock(), _createElementBlock("tr", _hoisted_5, [
              _createElementVNode("td", _hoisted_6, [
                _createElementVNode("div", _hoisted_7, [
                  (_unref(isLoading)(loadingState.value.status))
                    ? (_openBlock(), _createBlock(_unref(LottieAnimation), {
                        key: 0,
                        ref: "anim",
                        "animation-data": _unref(loadingAnimation),
                        "auto-play": true,
                        loop: true,
                        speed: 1
                      }, null, 8, ["animation-data"]))
                    : (_openBlock(), _createBlock(SvgLoader, {
                        key: 1,
                        class: _normalizeClass(`modal__icon`),
                        name: ""
                      })),
                  _createElementVNode("h1", _hoisted_8, _toDisplayString(_unref(t)('データを読み込んでいます')), 1)
                ])
              ])
            ]))
          : (_unref(isZeroMatch)(loadingState.value.status) || _unref(isNone)(loadingState.value.status))
            ? (_openBlock(), _createElementBlock("tr", _hoisted_9, [
                _createElementVNode("td", _hoisted_10, _toDisplayString(_unref(t)('該当するデータはありません')), 1)
              ]))
            : (_unref(isError)(loadingState.value.status))
              ? (_openBlock(), _createElementBlock("tr", _hoisted_11, [
                  _createElementVNode("td", _hoisted_12, [
                    _createTextVNode(_toDisplayString(_unref(t)('情報取得に失敗しました。時間を置いて再施行してください。')) + " ", 1),
                    _createElementVNode("button", {
                      class: "error__button",
                      onClick: _cache[0] || (_cache[0] = () => emits('reload'))
                    }, _toDisplayString(_unref(t)('再読込')), 1)
                  ])
                ]))
              : (_openBlock(true), _createElementBlock(_Fragment, { key: 3 }, _renderList(_ctx.rowMonthList, (receivedYearMonth, index) => {
                  return (_openBlock(), _createElementBlock("tr", {
                    key: index,
                    class: "timeline-table__row"
                  }, [
                    (index === 0)
                      ? (_openBlock(), _createElementBlock("th", {
                          key: 0,
                          class: "timeline-table__row-cell timeline-table__row-cell--span-title timeline-table__row-cell--sticky",
                          rowspan: _ctx.rowMonthList.length
                        }, _toDisplayString(_unref(t)('内示作成月')), 9, _hoisted_13))
                      : _createCommentVNode("", true),
                    _createElementVNode("th", _hoisted_14, _toDisplayString(_unref(DateService).convertIYearMonthToYYYYMMDivideSlash(receivedYearMonth)), 1),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.headerMonthList, (targetYearMonth, headerIndex) => {
                      return (_openBlock(), _createElementBlock("td", {
                        key: headerIndex,
                        class: _normalizeClass(["timeline-table__row-cell", getTableCellClass(receivedYearMonth, targetYearMonth, headerIndex)])
                      }, _toDisplayString(displayTableCell(receivedYearMonth, targetYearMonth)), 3))
                    }), 128))
                  ]))
                }), 128))
      ])
    ])
  ]))
}
}

})